import { IMessage } from "@endpoints/types/message.type";
import { chatMediaObjectUrl } from "@endpoints/images.service";
import dayjs from "@lib/dayjs";
import { RootState } from "@store/reducers";
import { EnumUserTypes } from "@utils/constants";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

type Attachment = NonNullable<IMessage["attachments"]>[number];

type MessageBubbleProps = {
  item: IMessage;
  receiverDetails: any;
};

const isImageAttachment = (attachment: Attachment) => {
  const mimeType = attachment?.mimeType || "";
  const fileName = attachment?.fileName || "";
  return mimeType.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName);
};

const MessageBubble = ({ item, receiverDetails }: MessageBubbleProps) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [resolvedAttachmentUrls, setResolvedAttachmentUrls] = useState<Record<string, string>>({});
  const isSender = item?.createdBy === (user?.userType === EnumUserTypes.CANDIDATE ? user?.userId : user?._id);
  const displayName = isSender ? (user?.firstName + ' ' + user?.lastName) : (receiverDetails?.name)
  // (user?.userType === EnumUserTypes.RECRUITER ? user?._id : user?.userId);

  useEffect(() => {
    let isMounted = true;
    const attachments = item?.attachments || [];

    const resolveUrls = async () => {
      const nextResolvedUrls: Record<string, string> = {};

      await Promise.all(
        attachments.map(async (attachment) => {
          if (!isImageAttachment(attachment)) return;

          if (attachment?.uid && attachment?.url) {
            nextResolvedUrls[attachment.uid] = attachment.url;
            return;
          }

          if (!attachment?.uid) return;

          try {
            const response = await chatMediaObjectUrl(attachment.uid);
            const url = response?.data?.url || response?.url || "";
            if (url) {
              nextResolvedUrls[attachment.uid] = url;
            }
          } catch (error) {
            console.error("Failed to resolve chat media url", error);
          }
        }),
      );

      if (isMounted) {
        setResolvedAttachmentUrls(nextResolvedUrls);
      }
    };

    resolveUrls();

    return () => {
      isMounted = false;
    };
  }, [item?.attachments]);

  return (
    <div
      className={`flex flex-col w-[60%] rounded-md p-3 gap-2 ${isSender ? "bg-white ml-auto" : "bg-[#F1F5F9] mr-auto"
        }`}
    >
      {/* {user?._id} */}
      <div className="flex items-center w-max">
        <span className="text-[10px] text-light-black font-medium">
          {dayjs(item?.createdAt).format("hh:mm a DD MMM")}
        </span>
        <span className="text-[10px] font-semibold text-light-black">
          &nbsp; -&nbsp; {displayName}
        </span>
      </div>
      {item?.message ? (
        <span className="text-xs font-medium text-charcoal whitespace-pre-wrap break-words">
          {item?.message}
        </span>
      ) : null}
      {item?.attachments?.length ? (
        <div className="flex flex-col gap-2">
          {item.attachments.map((attachment) => (
            <a
              key={attachment.uid}
              href={attachment.url}
              target="_blank"
              rel="noreferrer"
              className="block rounded-md border border-gray-200 bg-white overflow-hidden hover:shadow-sm"
            >
              {isImageAttachment(attachment) ? (
              <img
                  src={resolvedAttachmentUrls[attachment.uid] || attachment.url}
                  alt={attachment.fileName || "attachment"}
                  className="max-h-60 w-full object-cover"
                />
              ) : (
                <div className="flex items-center gap-3 p-3">
                  <div className="h-10 w-10 rounded bg-[#F1F5F9] flex items-center justify-center text-[10px] font-semibold uppercase text-charcoal">
                    {attachment.fileName?.split(".").pop()?.slice(0, 4) || "FILE"}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-charcoal truncate">
                      {attachment.fileName || "Attachment"}
                    </p>
                    <p className="text-[10px] text-light-black">
                      {attachment.mimeType || "Open file"}
                    </p>
                  </div>
                </div>
              )}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default MessageBubble;
